import { useEffect, useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'
import {
  type Case,
  type EntryLine,
  STATUS_LABELS,
  STATUS_ORDER,
  formatDate,
  formatUSD,
} from '@/lib/cases'
import { BarChart, DonutChart } from '@/components/app/charts'

const VOLUME_DAYS = 14

export function Reports() {
  const { user } = useAuth()
  const [cases, setCases] = useState<Case[]>([])
  const [lines, setLines] = useState<EntryLine[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    let active = true
    Promise.all([
      supabase.from('cases').select('*').eq('agent_id', user.id),
      supabase.from('entry_summary_lines').select('*'),
    ]).then(([c, l]) => {
      if (!active) return
      setCases((c.data ?? []) as Case[])
      setLines((l.data ?? []) as EntryLine[])
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [user])

  const volume = useMemo(() => {
    const today = new Date()
    const buckets: Array<{ label: string; value: number }> = []
    for (let i = VOLUME_DAYS - 1; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      const key = d.toISOString().slice(0, 10)
      const label = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      buckets.push({
        label,
        value: cases.filter((c) => c.created_at.startsWith(key)).length,
      })
    }
    return buckets
  }, [cases])

  const statusBreakdown = useMemo(
    () =>
      STATUS_ORDER.map((s) => ({
        label: STATUS_LABELS[s],
        value: cases.filter((c) => c.status === s).length,
      })).filter((d) => d.value > 0),
    [cases],
  )

  const topHts = useMemo(() => {
    const tally = new Map<string, { count: number; value: number; duty: number; description?: string }>()
    lines.forEach((l) => {
      const key = l.hts_code ?? '—'
      const cur = tally.get(key) ?? { count: 0, value: 0, duty: 0, description: l.description ?? '' }
      cur.count += 1
      cur.value += Number(l.line_total ?? 0)
      cur.duty += Number(l.duty_amount ?? 0)
      if (!cur.description && l.description) cur.description = l.description
      tally.set(key, cur)
    })
    return Array.from(tally.entries())
      .map(([code, v]) => ({ code, ...v }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [lines])

  const totals = useMemo(
    () => ({
      cases: cases.length,
      transmitted: cases.filter((c) => c.status === 'transmitted').length,
      enteredValue: cases.reduce((s, c) => s + Number(c.total_value_usd ?? 0), 0),
      duty: cases.reduce((s, c) => s + Number(c.total_duty_usd ?? 0), 0),
    }),
    [cases],
  )

  function exportCsv() {
    const header = [
      'case_number',
      'title',
      'status',
      'importer_of_record',
      'port_of_entry',
      'total_value_usd',
      'total_duty_usd',
      'flagged_count',
      'transmitted_at',
      'created_at',
      'updated_at',
    ]
    const rows = cases.map((c) =>
      header
        .map((k) => {
          const v = (c as unknown as Record<string, unknown>)[k]
          if (v == null) return ''
          const str = String(v).replace(/"/g, '""')
          return /[",\n]/.test(str) ? `"${str}"` : str
        })
        .join(','),
    )
    const csv = [header.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `vice-cases-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">Reports</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Volume, status, top HTS codes, and a CSV export of every case.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded border border-secondary text-secondary font-semibold hover:bg-secondary-lightest"
        >
          <Download size={16} aria-hidden="true" />
          Export CSV
        </button>
      </header>

      {/* Totals */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Total cases" value={loading ? '—' : totals.cases} />
        <Tile label="Transmitted" value={loading ? '—' : totals.transmitted} />
        <Tile label="Entered value" value={loading ? '—' : formatUSD(totals.enteredValue)} />
        <Tile label="Duty (estimated)" value={loading ? '—' : formatUSD(totals.duty)} />
      </div>

      {/* Volume + breakdown */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-4">
            Cases created — last {VOLUME_DAYS} days
          </h2>
          <BarChart data={volume} height={220} />
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-4">
            Status breakdown
          </h2>
          <DonutChart data={statusBreakdown} size={180} thickness={26} />
        </div>
      </div>

      {/* Top HTS */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
        <div className="px-5 py-3 border-b border-neutral-200">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
            Top HTS codes by entered value
          </h2>
        </div>
        {topHts.length === 0 ? (
          <p className="p-5 text-sm text-neutral-500">No line-item data yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-lightest text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-semibold px-4 py-2.5">HTS code</th>
                  <th className="text-left font-semibold px-4 py-2.5">Description</th>
                  <th className="text-right font-semibold px-4 py-2.5">Lines</th>
                  <th className="text-right font-semibold px-4 py-2.5">Total value</th>
                  <th className="text-right font-semibold px-4 py-2.5">Duty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {topHts.map((row) => (
                  <tr key={row.code}>
                    <td className="px-4 py-2.5 font-mono">{row.code}</td>
                    <td className="px-4 py-2.5">{row.description ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{row.count}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">
                      {formatUSD(row.value)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatUSD(row.duty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent transmitted */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
        <div className="px-5 py-3 border-b border-neutral-200">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
            Recently transmitted
          </h2>
        </div>
        {(() => {
          const sent = cases
            .filter((c) => c.status === 'transmitted')
            .sort((a, b) =>
              (b.transmitted_at ?? b.updated_at).localeCompare(a.transmitted_at ?? a.updated_at),
            )
            .slice(0, 5)
          if (sent.length === 0)
            return <p className="p-5 text-sm text-neutral-500">Nothing transmitted yet.</p>
          return (
            <ul className="divide-y divide-neutral-200">
              {sent.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-neutral-500">{c.case_number}</span>{' '}
                    <span className="font-semibold text-primary">{c.title}</span>
                  </div>
                  <span className="font-mono text-neutral-700">
                    {formatUSD(c.total_value_usd)}
                  </span>
                  <span className="text-xs text-neutral-500">
                    {formatDate(c.transmitted_at ?? c.updated_at)}
                  </span>
                </li>
              ))}
            </ul>
          )
        })()}
      </div>
    </section>
  )
}

function Tile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-primary">{value}</div>
    </div>
  )
}
